import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { conversationService } from '../services/api';
import Pagination from '../components/Pagination';
import { useI18n } from '../contexts/I18nContext';

const PhoneNumberSearch = () => {
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState(searchParams.get('phone') || '');
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: parseInt(searchParams.get('page') || '1', 10),
    totalPages: 1,
  });

  // Update pagination state when URL params change
  useEffect(() => {
    const page = parseInt(searchParams.get('page') || '1', 10);
    setPagination(prev => ({ ...prev, page }));
  }, [searchParams]);

  // Fetch conversations when phone number or page changes
  useEffect(() => {
    const phone = searchParams.get('phone');
    const page = parseInt(searchParams.get('page') || '1', 10);
    
    if (phone && phone.trim()) {
      fetchConversations(phone.trim(), page);
    } else {
      setConversations([]);
      setLoading(false);
    }
  }, [searchParams]);

  const fetchConversations = async (phone, pageToFetch = 1) => {
    try {
      setLoading(true);
      setError(null);
      const data = await conversationService.searchByPhoneNumber(phone, pageToFetch, 20);
      setConversations(data.conversations || []);
      setPagination({
        page: data.page,
        totalPages: data.total_pages,
      });
      } catch (err) {
      console.error('Error fetching conversations:', err);
      setError(t('phoneSearch.error'));
      setConversations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (!phoneNumber.trim()) {
      setError(t('phoneSearch.errorRequired'));
      return;
    }
    setSearchParams({
      phone: phoneNumber.trim(),
      page: '1',
    });
  };

  const handlePageChange = (newPage) => {
    setSearchParams({
      phone: phoneNumber.trim(),
      page: newPage.toString(),
    });
  };

  const handleConversationClick = (conversation) => {
    // Extract clinic ID from conversation_id (format: "concierge::{clid}::+{phone}")
    const parts = conversation.conversation_id.split('::');
    if (parts.length >= 2) {
      const clinicId = parts[1];
      navigate(`/clinics/${clinicId}/conversations?conversation=${encodeURIComponent(conversation.conversation_id)}`);
    }
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    // Format phone number for display
    return phone.replace(/(\+?)(\d{1,3})(\d{3})(\d{4,})/, '$1$2 $3 $4');
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">{t('phoneSearch.title')}</h2>
      </div>

      <form onSubmit={handleSearch} className="mb-4">
        <div className="flex gap-4 items-end">
          <div className="flex-grow">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('phoneSearch.phoneNumber')}
            </label>
            <input
              type="text"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder={t('phoneSearch.phoneNumberPlaceholder')}
              className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#075e54]"
            />
            <p className="mt-1 text-xs text-gray-500">
              {t('phoneSearch.phoneNumberHint')}
            </p>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-[#075e54] text-white rounded-md hover:bg-[#128c7e] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? t('phoneSearch.searching') : t('phoneSearch.search')}
          </button>
        </div>
      </form>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center h-[40vh]">
          <p className="text-gray-500">{t('phoneSearch.loading')}</p>
        </div>
      )}

      {!loading && !error && conversations.length > 0 && (
        <>
          <div className="bg-white rounded-lg shadow-md p-4">
            <p className="text-sm text-gray-600 mb-4">
              {pagination.totalPages > 1 
                ? `${t('phoneSearch.found')} ${conversations.length} ${t('phoneSearch.conversations')} ${t('phoneSearch.onThisPage')} (${t('phoneSearch.page')} ${pagination.page} ${t('phoneSearch.of')} ${pagination.totalPages})`
                : `${t('phoneSearch.found')} ${conversations.length} ${conversations.length !== 1 ? t('phoneSearch.conversations') : t('phoneSearch.conversation')}`
              }
            </p>
            <div className="space-y-3">
              {conversations.map((conversation) => (
                <div
                  key={conversation.conversation_id}
                  onClick={() => handleConversationClick(conversation)}
                  className="p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-grow">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-medium text-lg">
                          {conversation.profile_name || t('phoneSearch.unknown')}
                        </h3>
                        {conversation.clinic_name && (
                          <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                            {conversation.clinic_name}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-1">
                        {t('phoneSearch.phone')}: {formatPhoneNumber(conversation.from_number || conversation.conversation_id.split('::').pop())}
                      </p>
                      <p className="text-xs text-gray-500 mb-2">
                        {t('phoneSearch.lastMessage')}: {new Date(conversation.timestamp).toLocaleString()}
                      </p>
                      {conversation.message_content && (
                        <p className="text-sm text-gray-700 truncate">
                          {conversation.message_content}
                        </p>
                      )}
                    </div>
                    <div className="ml-4">
                      <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800">
                        {conversation.channel || 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {pagination.totalPages > 1 && (
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
              search={phoneNumber}
            />
          )}
        </>
      )}

      {!loading && !error && searchParams.get('phone') && conversations.length === 0 && (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <p className="text-gray-500 text-lg">{t('phoneSearch.noResults')}</p>
          <p className="text-gray-400 text-sm mt-2">{t('phoneSearch.noResultsHint')}</p>
        </div>
      )}

      {!loading && !error && !searchParams.get('phone') && (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <p className="text-gray-500 text-lg">{t('phoneSearch.enterPhone')}</p>
        </div>
      )}
    </div>
  );
};

export default PhoneNumberSearch;
